import { httpRequest } from '@/shared/api/client';
import { buildQueryString } from './builder';
import { denormalize, type Denormalized } from './parser';
import type { JsonApiDocument, JsonApiResource, ListQuery } from './types';
import { emitJsonApiWarnings } from './warnings';
import { applyScenarioB } from './simulazioneScenarioB';

// Contratto v1.7.0: attributi/relationships in camelCase sul wire (type e route restano
// kebab-case). Nessuna conversione di casing serve piu' lato FE.

export interface CreateBody<TAttributes = Record<string, unknown>> {
  /** Client-supplied id. If absent, a UUIDv4 is generated client-side. */
  id?: string;
  attributes?: TAttributes;
  relationships?: Record<string, { data: { type: string; id: string } | Array<{ type: string; id: string }> | null }>;
}

/** Options condivise dalle helper JSON:API. */
export interface JsonApiClientOptions {
  /**
   * Risorse con chiave primaria NON-GUID (es. `role-claims`, id intero identity):
   * NON inviare un id client-generated nel body POST, altrimenti il BE risponde
   * "Failed to deserialize request body: Incompatible 'id' value found". Con questo
   * flag l'id lo assegna il server (risposta 201 con body).
   */
  serverGeneratedId?: boolean;
}

/** Generate a UUIDv4 client-side. Workaround: il BE su alcune entità ritorna 404
 *  "ID '' not found" dopo la POST se l'Id non è esplicitato dal client. Lo
 *  generiamo qui per evitare race con il default `NEWID()` di SQL Server. */
export function generateUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback per ambienti senza crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export interface UpdateBody<TAttributes = Record<string, unknown>> extends CreateBody<TAttributes> {
  id: string;
}

/** Path conventions: `/api/<type>` (per JsonApiOptions.Namespace = "api" + UseRelativeLinks). */
function pathForType(type: string, id?: string): string {
  return id ? `/api/${type}/${id}` : `/api/${type}`;
}

export async function fetchList<T extends Denormalized = Denormalized>(
  type: string,
  query?: ListQuery,
): Promise<{ data: T[]; total: number; meta: Record<string, unknown> }> {
  const doc = await httpRequest<JsonApiDocument>(`${pathForType(type)}${buildQueryString(query)}`);
  const raw = (denormalize<T>(doc) as T[] | null) ?? [];
  // §5.3: spenta (sempre, in produzione) restituisce `grezzi` senza toccarli. Vedi il modulo.
  const data = applyScenarioB(raw, type, query?.include, query?.fields);
  return { data, total: typeof doc.meta?.total === 'number' ? doc.meta.total : data.length, meta: doc.meta ?? {} };
}

/**
 * Carica TUTTE le pagine di una lista JSON:API (page size BE max = **1000**, oltre → 400).
 * Utile per aggregazioni client-side che altrimenti si fermerebbero alla prima pagina.
 * `hardCap` evita loop runaway su dataset enormi (default 2000) **e** limita la pagina.
 */
export async function fetchAllPages<T extends Denormalized = Denormalized>(
  type: string,
  query?: Omit<ListQuery, 'page'>,
  hardCap = 2000,
): Promise<T[]> {
  /*
   * 1000, il massimo che il server accetta (misurato: 1001 -> 400). A 100 questa funzione
   * faceva dieci volte i giri necessari: su 3.860 commesse 39 richieste e 6.504 ms contro
   * 4 richieste e 4.356 ms. Il guadagno vero pero' viene dal `fields[]` sulla chiamata: senza,
   * una pagina da 1000 e' solo dieci volte il payload sbagliato tutto in una volta.
   *
   * `hardCap` fa da tetto anche alla pagina: chi chiama con `hardCap: 200` si e' dato un
   * limite di righe, e una prima pagina da 1000 lo sforerebbe di colpo — con `SIZE` fisso
   * quel tetto diventerebbe una decisione presa altrove.
   */
  const SIZE = Math.min(1000, Math.max(1, hardCap));
  const all: T[] = [];
  let page = 1;
  let total = Infinity;
  while (all.length < total && all.length < hardCap) {
    const res = await fetchList<T>(type, { ...query, page: { number: page, size: SIZE } });
    all.push(...res.data);
    total = typeof res.total === 'number' && res.total > 0 ? res.total : all.length;
    if (res.data.length < SIZE) break;
    page += 1;
  }
  if (all.length >= hardCap && total > all.length) reportTruncation(type, all.length, total);
  return all;
}

/**
 * IL TETTO CHE TAGLIA IN SILENZIO — e perché adesso parla.
 *
 * Quando il ciclo qui sopra si ferma per `hardCap`, questa funzione restituisce un array che
 * **sembra completo**: nessun errore, nessun flag, nessun modo per il chiamante di accorgersene.
 * Il risultato è un elenco incompleto trattato come se fosse tutto — e da lì una ricerca in
 * memoria risponde «nessun risultato» per righe che esistono. Che è peggio di un errore: è una
 * risposta sbagliata detta con sicurezza.
 *
 * Il primo settembre 2026 lo abbiamo trovato **tre volte in una notte**, e tutte e tre per caso
 * guardando una cascata di rete: 3.292 commesse nella sidebar dell'agenda (1.292 perse), 3.860
 * nella tendina del filtro dei task (1.860 perse), 2.867 permessi nella console dei ruoli (867
 * persi, su una schermata che decide chi può fare cosa). **Novantuno dei 123 punti di chiamata**
 * si prendono il tetto di default senza averlo scelto.
 *
 * ⚠️ Un `console.warn` e non un errore, di proposito: a volte il tetto è **voluto** — chi passa
 * `hardCap: 200` su un catalogo si è dato un limite e sa quello che fa. Distinguere le due
 * intenzioni dal di dentro non si può; farle vedere entrambe a chi sviluppa, sì.
 *
 * In produzione non esiste: il cancello è `import.meta.env.DEV`, quindi il bundle spedito non
 * porta né questa funzione né la sua chiamata.
 */
function reportTruncation(type: string, read: number, totali: number): void {
  if (!import.meta.env.DEV) return;
  console.warn(
    `[fetchAllPages] «${type}» tagliata: ${read} righe lette su ${totali}. ` +
    `Le altre ${totali - read} non arrivano, e chi le cerca non le trova. ` +
    'Se il tetto è voluto, passalo esplicito e dillo a schermo; se non lo è, alzalo — ' +
    'e metti un `fields[]`, che è ciò che rende sostenibile una lettura larga.',
  );
}

export async function fetchOne<T extends Denormalized = Denormalized>(
  type: string,
  id: string,
  query?: Pick<ListQuery, 'include' | 'fields' | 'params'>
): Promise<T | null> {
  // GET single: niente injection — il BE risolve il tenant dal GUID (lookup
  // IgnoreQueryFilters), poi valida claim + descendant.
  const doc = await httpRequest<JsonApiDocument>(`${pathForType(type, id)}${buildQueryString(query)}`);
  // §5.3: vale anche sul singolo — la §7.2 era proprio un `fetchOne` senza include.
  return applyScenarioB(denormalize<T>(doc) as T | null, type, query?.include, query?.fields);
}

export async function createOne<T extends Denormalized = Denormalized>(
  type: string,
  body: CreateBody,
  opts?: JsonApiClientOptions,
): Promise<T | null> {
  const id = opts?.serverGeneratedId ? undefined : (body.id ?? generateUuid());
  const payload: { data: Omit<JsonApiResource, 'id'> & { id?: string } } = {
    data: {
      type,
      ...(id ? { id } : {}),
      attributes: body.attributes,
      relationships: body.relationships,
    },
  };
  const doc = await httpRequest<JsonApiDocument | undefined>(pathForType(type), { method: 'POST', body: payload });
  // Il `meta` di primo livello va letto PRIMA di denormalizzare, che tiene solo `data`:
  // su una 2xx può portare avvisi su ciò che è riuscito a metà (vedi `warnings.ts`).
  emitJsonApiWarnings(doc, { resourceType: type, id, operation: 'create' });
  // JsonApiDotNetCore restituisce 204 No Content per POST con client-generated ID:
  // niente body in response, ma la risorsa e' stata creata con l'id che abbiamo
  // generato. Ricostruiamo lo stub dal payload locale cosi' il caller riceve sempre
  // un {id,type,...attributes} usable (la React Query subito dopo fara' la fetch
  // completa per il reload). Se il server invece restituisce un documento (201),
  // denormalizziamo come al solito.
  if (!doc) {
    return { type, id, ...body.attributes } as unknown as T;
  }
  return denormalize<T>(doc) as T | null;
}

export async function updateOne<T extends Denormalized = Denormalized>(type: string, body: UpdateBody): Promise<T | null> {
  // PATCH: niente injection cross-tenant — il BE risolve il tenant dal GUID
  // (lookup IgnoreQueryFilters), poi valida claim + descendant.
  const payload: { data: JsonApiResource } = {
    data: { type, id: body.id, attributes: body.attributes, relationships: body.relationships },
  };
  const doc = await httpRequest<JsonApiDocument | undefined>(pathForType(type, body.id), { method: 'PATCH', body: payload });
  emitJsonApiWarnings(doc, { resourceType: type, id: body.id, operation: 'update' });
  // PATCH 204 → ricostruisco stub coerente con createOne (vedi nota sopra).
  if (!doc) {
    return { type, id: body.id, ...body.attributes } as unknown as T;
  }
  return denormalize<T>(doc) as T | null;
}

export interface DeleteOptions {
  /**
   * BE v1.8.2+: opt-in per cancellare anche i record dipendenti invece di ricevere 409.
   * Supportato sulle erp-sites (?cascade=true elimina dipendenti/orari/sedi figlie).
   */
  cascade?: boolean;
}

export async function deleteOne(type: string, id: string, opts?: DeleteOptions): Promise<void> {
  // DELETE: niente injection cross-tenant — il BE risolve il tenant dal GUID.
  const qs = opts?.cascade ? '?cascade=true' : '';
  await httpRequest(`${pathForType(type, id)}${qs}`, { method: 'DELETE' });
}
